import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { FolderPlus, Download, Trash2, FolderOpen, Link2, Upload, File as FileIcon, FilePlus } from "lucide-react";
import { toast } from "sonner";

export default function MaterialiPage() {
  const { user } = useAuth();
  const [materiali, setMateriali] = useState([]);
  const [classi, setClassi] = useState([]);
  const [materie, setMaterie] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ titolo: "", tipo: "dispensa", descrizione: "", materia: "" });
  const [folder, setFolder] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [assocMat, setAssocMat] = useState(null);
  const [assocSel, setAssocSel] = useState([]);
  const [singleOpen, setSingleOpen] = useState(false);
  const emptySingle = { titolo: "", tipo: "dispensa", descrizione: "", materia: "", classi_ids: [], fileObj: null };
  const [single, setSingle] = useState(emptySingle);
  const [savingSingle, setSavingSingle] = useState(false);
  const canManage = user?.role === "admin" || user?.role === "docente" || user?.role === "collaboratore";
  const token = () => localStorage.getItem("isteform_token");

  const load = async (keepFolderId) => {
    const [m, c, mt] = await Promise.all([api.get("/materiali"), api.get("/classi"), api.get("/materie")]);
    setMateriali(m.data); setClassi(c.data); setMaterie(mt.data);
    if (keepFolderId) setFolder(m.data.find(x => x.id === keepFolderId) || null);
  };
  useEffect(() => { load(); }, []);

  const createFolder = async () => {
    if (!form.titolo.trim()) { toast.error("Inserisci un titolo"); return; }
    try {
      const { data } = await api.post("/materiali", form);
      toast.success("Cartella creata"); setOpen(false); setForm({ titolo: "", tipo: "dispensa", descrizione: "", materia: "" });
      await load(); setFolder(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const toggleSingleClasse = (id) => setSingle(s => ({ ...s, classi_ids: s.classi_ids.includes(id) ? s.classi_ids.filter(x => x !== id) : [...s.classi_ids, id] }));
  const saveSingle = async () => {
    if (!single.fileObj) { toast.error("Seleziona un file"); return; }
    setSavingSingle(true);
    try {
      const fd = new FormData();
      fd.append("file", single.fileObj);
      fd.append("titolo", single.titolo);
      fd.append("materia", single.materia);
      fd.append("tipo", single.tipo);
      fd.append("descrizione", single.descrizione);
      fd.append("classi_ids", single.classi_ids.join(","));
      const t = token();
      const r = await fetch(`${API}/materiali/file-singolo`, { method: "POST", body: fd, credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {} });
      if (!r.ok) throw new Error();
      toast.success("File caricato"); setSingleOpen(false); setSingle(emptySingle); await load();
    } catch { toast.error("Errore upload"); }
    finally { setSavingSingle(false); }
  };

  const addFile = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0 || !folder) return;
    setUploading(true);
    let ok = 0, ko = 0;
    for (const fileObj of files) {
      const fd = new FormData(); fd.append("file", fileObj);
      try {
        const t = token();
        const r = await fetch(`${API}/materiali/${folder.id}/files`, { method: "POST", body: fd, credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {} });
        if (!r.ok) throw new Error();
        ok++;
      } catch { ko++; }
    }
    if (ok) toast.success(`${ok} file caricat${ok===1?"o":"i"}${ko?` · ${ko} errori`:""}`);
    else toast.error("Errore upload");
    await load(folder.id);
    setUploading(false);
  };

  const removeFile = async (fid) => {
    if (!window.confirm("Rimuovere il file?")) return;
    await api.delete(`/materiali/${folder.id}/files/${fid}`); toast.success("File rimosso"); await load(folder.id);
  };

  const download = async (fid, name) => {
    const t = token();
    const r = await fetch(`${API}/materiali/${folder.id}/files/${fid}/download`, { credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {} });
    const blob = await r.blob();
    const link = document.createElement("a"); link.href = URL.createObjectURL(blob); link.download = name; link.click();
  };

  const del = async (id) => { if (!window.confirm("Eliminare la cartella e tutti i suoi file?")) return; await api.delete(`/materiali/${id}`); toast.success("Eliminata"); if (folder?.id === id) setFolder(null); load(); };

  const openAssoc = (m) => { setAssocMat(m); setAssocSel([m.classe_id, ...(m.classi_ids || [])].filter(Boolean)); };
  const toggleAssoc = (id) => setAssocSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const saveAssoc = async () => {
    try { await api.put(`/materiali/${assocMat.id}/classi`, { classi_ids: assocSel }); toast.success("Associazioni aggiornate"); setAssocMat(null); await load(); }
    catch { toast.error("Errore associazione"); }
  };

  const tipoBadge = { slide: "bg-blue-50 text-blue-800", video: "bg-purple-50 text-purple-800", dispensa: "bg-amber-50 text-amber-900", altro: "bg-stone-100 text-stone-700" };
  const classeNomi = (m) => [m.classe_id, ...(m.classi_ids || [])].filter(Boolean).map(id => classi.find(c => c.id === id)?.nome).filter(Boolean);

  return (
    <div className="p-4 sm:p-8">
      <div className="flex flex-wrap gap-4 items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F7941E] mb-2">Hub Didattico</div>
          <h1 className="text-3xl font-bold text-stone-900">Materiali Didattici</h1>
          <p className="text-stone-600 mt-1">Cartelle con più file · l'associazione alle classi avviene dopo</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
          <Dialog open={singleOpen} onOpenChange={(o)=>{setSingleOpen(o); if(!o) setSingle(emptySingle);}}>
            <DialogTrigger asChild><Button variant="outline" className="border-[#1F4E79] text-[#1F4E79]" data-testid="new-file-singolo-btn"><FilePlus className="w-4 h-4 mr-2"/>File Singolo</Button></DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Carica file singolo</DialogTitle><DialogDescription>Un solo file, con materia, associabile subito a una o più classi.</DialogDescription></DialogHeader>
              <div className="grid gap-4">
                <div><Label>Titolo (opzionale)</Label><Input data-testid="single-titolo-input" value={single.titolo} onChange={e=>setSingle({...single,titolo:e.target.value})} placeholder="Se vuoto usa il nome del file"/></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Tipo</Label>
                    <Select value={single.tipo} onValueChange={v=>setSingle({...single,tipo:v})}>
                      <SelectTrigger data-testid="single-tipo"><SelectValue/></SelectTrigger>
                      <SelectContent><SelectItem value="dispensa">Dispensa</SelectItem><SelectItem value="slide">Slide</SelectItem><SelectItem value="video">Video</SelectItem><SelectItem value="altro">Altro</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Materia</Label>
                    <Select value={single.materia} onValueChange={v=>setSingle({...single,materia:v})}>
                      <SelectTrigger data-testid="single-materia"><SelectValue placeholder="Seleziona"/></SelectTrigger>
                      <SelectContent>{materie.map(m=><SelectItem key={m.id} value={m.nome}>{m.nome}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Descrizione</Label><Textarea value={single.descrizione} onChange={e=>setSingle({...single,descrizione:e.target.value})}/></div>
                <div>
                  <Label>Classi associate</Label>
                  <div className="border border-[#E2E8F0] rounded-lg max-h-40 overflow-y-auto p-2 space-y-1 mt-1">
                    {classi.map(c => (
                      <label key={c.id} className="flex items-center gap-2 p-1.5 hover:bg-[#FFF5EB] rounded cursor-pointer">
                        <input type="checkbox" checked={single.classi_ids.includes(c.id)} onChange={()=>toggleSingleClasse(c.id)} data-testid={`single-classe-${c.id}`}/>
                        <span className="text-sm">{c.nome}</span>
                      </label>
                    ))}
                    {classi.length === 0 && <div className="text-xs text-stone-400 p-1">Nessuna classe disponibile</div>}
                  </div>
                </div>
                <div><Label>File</Label><Input type="file" onChange={e=>setSingle({...single, fileObj:e.target.files?.[0] || null})} data-testid="single-file-input"/></div>
              </div>
              <DialogFooter><Button onClick={saveSingle} disabled={savingSingle} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="single-save-btn">{savingSingle ? "Caricamento..." : "Carica file"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="new-cartella-btn"><FolderPlus className="w-4 h-4 mr-2"/>Nuova Cartella</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuova Cartella</DialogTitle><DialogDescription>Crea una cartella; potrai caricare più file e associarla alle classi in un secondo momento.</DialogDescription></DialogHeader>
              <div className="grid gap-4">
                <div><Label>Titolo</Label><Input data-testid="cartella-titolo-input" value={form.titolo} onChange={e=>setForm({...form,titolo:e.target.value})}/></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Tipo</Label>
                    <Select value={form.tipo} onValueChange={v=>setForm({...form,tipo:v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent><SelectItem value="dispensa">Dispensa</SelectItem><SelectItem value="slide">Slide</SelectItem><SelectItem value="video">Video</SelectItem><SelectItem value="altro">Altro</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Materia</Label>
                    <Select value={form.materia} onValueChange={v=>setForm({...form,materia:v})}>
                      <SelectTrigger data-testid="cartella-materia"><SelectValue placeholder="Seleziona"/></SelectTrigger>
                      <SelectContent>{materie.map(m=><SelectItem key={m.id} value={m.nome}>{m.nome}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Descrizione</Label><Textarea value={form.descrizione} onChange={e=>setForm({...form,descrizione:e.target.value})}/></div>
              </div>
              <DialogFooter><Button onClick={createFolder} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="cartella-save-btn">Crea cartella</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {materiali.map(m => {
          const nomi = classeNomi(m);
          return (
            <Card key={m.id} className="p-4 bg-white border-[#E2E8F0]" data-testid={`materiale-${m.id}`}>
              <div className="flex justify-between mb-3">
                <Badge className={tipoBadge[m.tipo]}>{m.tipo}</Badge>
                <FolderOpen className="w-5 h-5 text-[#F7941E]"/>
              </div>
              <div className="font-semibold text-stone-900">{m.titolo}</div>
              {m.materia && <div className="text-xs font-medium text-[#1F4E79] mt-0.5" data-testid={`materiale-materia-${m.id}`}>Materia: {m.materia}</div>}
              {m.descrizione && <div className="text-xs text-stone-500 mt-0.5">{m.descrizione}</div>}
              <div className="text-xs text-stone-500 mt-1" data-testid={`materiale-filecount-${m.id}`}>{(m.files || []).length} file</div>
              <div className="text-xs mt-1">{nomi.length ? <span className="text-[#F7941E]">{nomi.join(", ")}</span> : <span className="text-stone-400">Nessuna classe associata</span>}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={()=>setFolder(m)} className="flex-1" data-testid={`apri-cartella-${m.id}`}><FolderOpen className="w-3 h-3 mr-1"/>Apri</Button>
                {canManage && <Button size="sm" variant="ghost" onClick={()=>openAssoc(m)} title="Associa a classi" data-testid={`assoc-materiale-${m.id}`}><Link2 className="w-3 h-3 text-[#F7941E]"/></Button>}
                {canManage && <Button size="sm" variant="ghost" onClick={()=>del(m.id)} data-testid={`del-materiale-${m.id}`}><Trash2 className="w-3 h-3 text-red-500"/></Button>}
              </div>
            </Card>
          );
        })}
        {materiali.length === 0 && <div className="col-span-full text-center text-stone-500 py-12">Nessuna cartella creata.</div>}
      </div>

      <Dialog open={!!folder} onOpenChange={(o)=>{if(!o) setFolder(null);}}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{folder?.titolo}</DialogTitle><DialogDescription>Contenuto della cartella. {canManage ? "Aggiungi o rimuovi file." : "Scarica i file disponibili."}</DialogDescription></DialogHeader>
          {folder && (
            <div className="space-y-2" data-testid="cartella-files">
              {(folder.files || []).map(f => (
                <div key={f.file_id} className="flex items-center gap-2 border border-[#E2E8F0] rounded-lg p-2" data-testid={`file-row-${f.file_id}`}>
                  <FileIcon className="w-4 h-4 text-stone-400 shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{f.original_filename}</div>
                    <div className="text-xs text-stone-400">{(f.size/1024).toFixed(1)} KB</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={()=>download(f.file_id, f.original_filename)} data-testid={`download-file-${f.file_id}`}><Download className="w-4 h-4 text-[#1F4E79]"/></Button>
                  {canManage && <Button size="sm" variant="ghost" onClick={()=>removeFile(f.file_id)} data-testid={`remove-file-${f.file_id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>}
                </div>
              ))}
              {(folder.files || []).length === 0 && <div className="text-center text-sm text-stone-500 py-6">Cartella vuota.</div>}
            </div>
          )}
          {canManage && folder && (
            <div className="mt-2">
              <Label className="text-sm">Aggiungi file</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="file" multiple onChange={e=>{ if(e.target.files.length){ addFile(e.target.files); e.target.value=""; } }} disabled={uploading} data-testid="cartella-add-file-input"/>
                {uploading && <span className="text-xs text-stone-500 flex items-center gap-1"><Upload className="w-3 h-3 animate-pulse"/>...</span>}
              </div>
              <Label className="text-sm mt-3 block">Oppure carica un'intera cartella dal computer</Label>
              <input type="file" multiple ref={el=>{ if(el){ el.webkitdirectory=true; el.directory=true; } }}
                onChange={e=>{ if(e.target.files.length){ addFile(e.target.files); e.target.value=""; } }} disabled={uploading}
                data-testid="cartella-upload-folder-input"
                className="mt-1 block w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#FFF5EB] file:text-[#C96F0E]"/>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!assocMat} onOpenChange={(o)=>{if(!o) setAssocMat(null);}}>
        <DialogContent>
          <DialogHeader><DialogTitle>Associa a classi</DialogTitle><DialogDescription>La cartella "{assocMat?.titolo}" sarà visibile agli studenti e ai docenti delle classi selezionate.</DialogDescription></DialogHeader>
          <div className="border border-[#E2E8F0] rounded-lg max-h-64 overflow-y-auto p-2 space-y-1">
            {classi.map(c => (
              <label key={c.id} className="flex items-center gap-2 p-2 hover:bg-[#FFF5EB] rounded cursor-pointer">
                <input type="checkbox" checked={assocSel.includes(c.id)} onChange={()=>toggleAssoc(c.id)} data-testid={`assoc-classe-${c.id}`}/>
                <span className="text-sm">{c.nome}</span>
              </label>
            ))}
          </div>
          <DialogFooter><Button onClick={saveAssoc} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="assoc-save-btn">Salva associazioni</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
